import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/button';
import ConnectedAccount from '../ConnectedAccount';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { RootState } from '../../../../reducers';
import { getInitialState } from '../../../app/appSlice';
import {
  initMicrosoftTeamsCalendar,
  disconnectMicrosoftTeamsCalendar,
} from '../../../../services/integrationService';
import MicrosoftIcon from '../../../../assets/icons/microsoftlogo.svg';

const MicrosoftTeamsCalendarIntegration = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => ({
    user: state?.settings.user,
  }));

  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const { isActive, personEmail, personName } =
    user.integrations?.microsoft?.calendar || {};

  const handleConnectMicrosoftTeamsCalendar = async () => {
    const response = await initMicrosoftTeamsCalendar();
    window.location.href = response.oauthUrl;
  };

  const handleDisconnectMicrosoftTeamsCalendar = async () => {
    setIsDisconnectDialogOpen(false);
    await disconnectMicrosoftTeamsCalendar();
    dispatch(getInitialState());
  };

  if (!isActive) {
    return (
      <div className="flex items-center gap-4">
        <Button
          variant="minimal"
          onClick={handleConnectMicrosoftTeamsCalendar}
          data-testid="connect-microsoft-teams-calendar-button"
          className="text-black hover:text-black"
        >
          {t('settings.meetingBot.microsoftTeams.connectButton', 'Connect')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <ConnectedAccount
        name={personName}
        email={personEmail}
        onDisconnect={() => setIsDisconnectDialogOpen(true)}
        icon={MicrosoftIcon}
      />

      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                'settings.meetingBot.microsoftTeams.disconnectDialogTitle',
                'Disconnect Microsoft Teams Calendar?',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                'settings.meetingBot.microsoftTeams.disconnectDialogMessage',
                'This will stop automatic meeting recordings. You can reconnect your calendar at any time.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setIsDisconnectDialogOpen(false)}
              variant="outline"
              className="min-w-[100px]"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleDisconnectMicrosoftTeamsCalendar}
              variant="destructive"
              className="min-w-[100px]"
            >
              {t('settings.meetingBot.microsoftTeams.disconnect', 'Disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MicrosoftTeamsCalendarIntegration;
